import React, { useState, useEffect } from 'react';
import { Box, Fab, Badge, Drawer } from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';
import Chat from './index';
import { socket } from '../../../../api';
import { getUnreadCount } from '../../../../api/chat';

export default function ChatWidget({ access, user, socketStatus }) {
  const [open, setOpen] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);

  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        const count = await getUnreadCount();
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to initialize chat:', error);
        setUnreadCount(0);
      }
      

    };
    
    initializeChat();
    


  }, []);

  useEffect(() => {
    if (!socket) return;
    
    const handleNewMessage = (data) => {
      try {
        if (data?.coll === 'messages' && data?.type === 'insert' && data?.message) {
          const isOwnMessage = String(data.message.senderId) === String(user?.id);
          
          if (!isOwnMessage) {
            // If the message is for the currently open conversation and chat is open, mark as read
            if (open && currentConversationId === data.message.conversationId) {
              const { markConversationRead } = require('../../../../api/chat');
              markConversationRead(data.message.conversationId).catch(console.error);
            }
            
            // Always refresh unread count for messages from others
            getUnreadCount().then(count => setUnreadCount(count)).catch(console.error);
          }
        }
      } catch (error) {
        console.error('Error handling new message:', error);
      }
    };
    
    const handleSocketError = (error) => {
      console.error('Socket error:', error);
    };
    

    
    const handleMessageRead = () => {
      getUnreadCount().then(count => setUnreadCount(count)).catch(console.error);
    };
    
    socket.on('sub', handleNewMessage);
    socket.on('error', handleSocketError);

    socket.on('messageRead', handleMessageRead);
    
    socket.emit('sub', { coll: 'messages', type: 'insert' });
    

    
    getUnreadCount().then(count => setUnreadCount(count)).catch(console.error);
    
    return () => {
      // Server automatically handles presence on disconnect
      socket.off('sub', handleNewMessage);
      socket.off('error', handleSocketError);

      socket.off('messageRead', handleMessageRead);
      socket.emit('unsub', { coll: 'messages', type: 'insert' });
    };
  }, [open, user?.id, currentConversationId]);

  // Refresh unread count and emit presence when socket reconnects
  useEffect(() => {
    if (socketStatus) {
      getUnreadCount().then(count => setUnreadCount(count)).catch(console.error);

    }
  }, [socketStatus, user?.id]);

  // No periodic updates needed - server only broadcasts actual status changes
  // Presence is maintained through socket connection state and reconnection events

  const handleOpen = () => {
    setOpen(true);
    // Refresh unread count when opening chat
    getUnreadCount().then(count => setUnreadCount(count)).catch(console.error);
  };

  const handleConversationRead = () => {
    // Refresh unread count when a conversation is marked as read
    getUnreadCount().then(count => setUnreadCount(count)).catch(console.error);
  };

  return (
    <>
      <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
        <Badge badgeContent={unreadCount} color="error">
          <Fab 
            onClick={handleOpen}
            size="medium"
            sx={{ 
              bgcolor: 'black', 
              color: 'white',
              border: '1px solid white',
              '&:hover': { bgcolor: 'grey.800' },
              boxShadow: 3,
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 }
            }}
          >
            <ChatIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </Fab>
        </Badge>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { 
            width: { xs: '100%', sm: 600, md: 800 }, 
            height: '100dvh',
            maxHeight: '100dvh',
            overflow: 'hidden'
          }
        }}
      >
        <Chat 
          access={access} 
          user={user} 
          onConversationRead={handleConversationRead} 
          socketStatus={socketStatus} 
          selectedConversation={selectedConversation}
          onConversationChange={(conversationId) => {
            setCurrentConversationId(conversationId);
            setSelectedConversation(conversationId);
          }}
          onClose={() => setOpen(false)}
        />
      </Drawer>
    </>
  );
}
